// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { makeStyles, tokens } from '@fluentui/react-components';
import { assert, CoursePlatform } from '@microsoft/vscodeedu-api';
import React, { useMemo } from 'react';
import { useConfig } from '../../data';
import { getContentUrl } from '../../utilities';

const useStyles = makeStyles({
    root: {
        width: '100%',
        aspectRatio: '220/93',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: tokens.borderRadiusMedium,
    },
});

// Curriculum image cell properties.
export type ImageCellProps = Readonly<{
    cardImageUrl?: string;
    platform: CoursePlatform;
}>;

// Curriculum image data grid cell contents.
export default (props: ImageCellProps) => {
    const { cardImageUrl, platform } = props;
    const styles = useStyles();
    const { contentBaseUrl } = useConfig();

    const url = useMemo(() => {
        if (cardImageUrl === undefined) {
            switch (platform) {
                case 'html':
                    return getContentUrl(contentBaseUrl, 'assets/course-cards/html.png');
                case 'python':
                    return getContentUrl(contentBaseUrl, 'assets/course-cards/python.png');
                default:
                    assert.unreachable();
            }
        } else {
            return cardImageUrl;
        }
    }, [contentBaseUrl, cardImageUrl, platform]);

    return <div className={styles.root} aria-hidden style={{ backgroundImage: `url('${url}')` }} />;
};
